import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const BrandingContext = createContext();

// === Google Analytics / Tag Manager injection ===
// inject เพียงครั้งเดียวต่ออายุ tab — หาก ID เปลี่ยนภายหลังก็ไม่ inject ทับ (ต้อง refresh)
let _gaInjectedId = null;

// โดเมนสำรองสำหรับ cross-domain measurement หากยังไม่ได้ตั้งค่าใน Admin Settings
const DEFAULT_CROSS_DOMAINS = ['imoney.sskh.app', 'imoney.sisakethospital.com'];

// แปลงค่าจาก DB (string คั่นด้วยจุลภาค) เป็น array ของ hostname
function parseCrossDomains(raw) {
    if (!raw || typeof raw !== 'string') return DEFAULT_CROSS_DOMAINS;
    const list = raw
        .split(/[\s,]+/)
        .map(d => d.trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, ''))
        .filter(Boolean);
    return list.length > 0 ? list : DEFAULT_CROSS_DOMAINS;
}

function injectGoogleAnalytics(id, crossDomains = DEFAULT_CROSS_DOMAINS) {
    if (!id || typeof window === 'undefined') return;
    if (_gaInjectedId === id) return;
    if (_gaInjectedId && _gaInjectedId !== id) {
        // ID เปลี่ยน — ไม่ลบของเก่า ปล่อยให้ refresh ทีหลัง
        return;
    }
    _gaInjectedId = id;

    // GTM container (GTM-XXXXX)
    if (/^GTM-/i.test(id)) {
        const s1 = document.createElement('script');
        s1.async = true;
        s1.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`;
        document.head.appendChild(s1);

        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.insertBefore(noscript, document.body.firstChild);
        return;
    }

    // GA4 (G-XXXXXXXXXX) หรือ Universal Analytics (UA-XXXX)
    const s1 = document.createElement('script');
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(s1);

    const s2 = document.createElement('script');
    const domainsJson = JSON.stringify(crossDomains);
    s2.innerHTML = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${id}', { anonymize_ip: true, linker: { domains: ${domainsJson} } });`;
    document.head.appendChild(s2);
}

export function BrandingProvider({ children }) {
    const [branding, setBranding] = useState({
        app_name: 'iMoney',
        app_credit: 'by terza',
        google_analytics_id: '',
        ga_cross_domains: '',
        identity_verify_mode: 'face_card',
        identity_photo_check: true,
        providerid_enabled: false
    });
    const [loading, setLoading] = useState(true);

    const fetchBranding = useCallback(async () => {
        try {
            const res = await api.get('/branding');
            const data = {
                app_name: res.data.app_name || 'iMoney',
                app_credit: res.data.app_credit || 'by terza',
                google_analytics_id: res.data.google_analytics_id || '',
                ga_cross_domains: res.data.ga_cross_domains || '',
                // โหมดยืนยันตัวตนด้วยรูป (หน้าสมัคร/ขอรหัสใหม่) — ตั้งค่าได้ในหน้าตั้งค่าระบบ
                identity_verify_mode: res.data.identity_verify_mode === 'card_only' ? 'card_only' : 'face_card',
                identity_photo_check: res.data.identity_photo_check !== 'false',
                // ปุ่ม "เข้าสู่ระบบด้วย Provider ID" — server เปิดให้เมื่อตั้งค่า client id/secret ครบใน .env
                providerid_enabled: res.data.providerid_enabled === true
            };
            setBranding(data);

            // ถ้ามี GA ID ให้ inject script (ครั้งแรกที่โหลด)
            if (data.google_analytics_id) {
                injectGoogleAnalytics(data.google_analytics_id, parseCrossDomains(data.ga_cross_domains));
            }
        } catch (err) {
            console.error('Error fetching branding:', err);
            // Keep defaults on error
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBranding();

        // Listen for branding updates from AdminSettings
        const handleBrandingUpdate = () => {
            fetchBranding();
        };

        window.addEventListener('branding-updated', handleBrandingUpdate);

        return () => {
            window.removeEventListener('branding-updated', handleBrandingUpdate);
        };
    }, [fetchBranding]);

    const value = {
        appName: branding.app_name,
        appCredit: branding.app_credit,
        googleAnalyticsId: branding.google_analytics_id,
        // true = ต้องแนบรูปถือบัตรด้วย, false = แนบรูปบัตรอย่างเดียว
        requireFacePhoto: branding.identity_verify_mode !== 'card_only',
        // true = ตรวจรูปอัตโนมัติก่อนแนบ (ImageCapture check=)
        photoCheckEnabled: branding.identity_photo_check,
        providerIdEnabled: branding.providerid_enabled,
        loading,
        refresh: fetchBranding
    };

    return (
        <BrandingContext.Provider value={value}>
            {children}
        </BrandingContext.Provider>
    );
}

export function useBranding() {
    const context = useContext(BrandingContext);
    if (!context) {
        throw new Error('useBranding must be used within a BrandingProvider');
    }
    return context;
}

export default BrandingContext;
